import uuid from './uuid';
import { clearDb } from './db';

/**
 * Persisted copies of a budget's data, one record per (namespace, schema, user, budget), in the
 * local-first database's `snapshots` store. Generic: food is its first user and transactions its
 * second, and nothing here knows what a record holds beyond `version` and `savedAt`.
 *
 * Several tabs write the same records, so every write is guarded in its own transaction:
 *
 * - by the session marker (`meta.session`): a write lands only while the marker names the writing
 *   tab's user and session. Logout removes it first, so a tab still open from before writes nothing.
 * - by version: a record's `version` is `[generatedAt, mergeCount]`, and a write lands only when its
 *   version is not lower than the stored one, so a background tab with older data cannot overwrite
 *   a newer copy.
 */

const SNAPSHOTS = 'snapshots';
const META = 'meta';
const SESSION = 'session';

/** `<namespace>:v<schema>:u<userId>:b<budgetId>` */
export const recordKey = ({ namespace, schema, userId, budgetId }) => `${namespace}:v${schema}:u${userId}:b${budgetId}`;

/** recordKey()'s parts, or null for a key it did not make. */
export const parseRecordKey = (key) => {
	const match = /^([^:]+):v(\d+):u([^:]+):b(.+)$/.exec(String(key));
	if(!match) {
		return null;
	}
	return { namespace: match[1], schema: Number(match[2]), userId: match[3], budgetId: match[4] };
};

/** Compares two versions element by element: negative, zero or positive. A missing version is lowest. */
export const compareVersions = (a, b) => {
	const left = Array.isArray(a) ? a : [];
	const right = Array.isArray(b) ? b : [];
	for(let i = 0; i < Math.max(left.length, right.length); i++) {
		const difference = (Number(left[i]) || 0) - (Number(right[i]) || 0);
		if(difference !== 0) {
			return difference;
		}
	}
	return 0;
};

const sameUser = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

export function createPersistedStore({ db, newSessionId = uuid }) {
	return {
		get persistent() {
			return !!db.persistent;
		},

		/** The record stored under `key`, or null. */
		load(key) {
			return db.run([SNAPSHOTS], 'readonly', (tx) => tx.get(SNAPSHOTS, key))
				.then((record) => record || null);
		},

		/**
		 * Writes `record` under `key` when `session` is still the stored session and the record's
		 * version is not lower than the stored one. Resolves whether it wrote.
		 */
		save(key, record, session) {
			return db.run([SNAPSHOTS, META], 'readwrite', (tx) => tx.get(META, SESSION).then((current) => {
				if(!session || !current || !sameUser(current.userId, session.userId) || current.sessionId !== session.sessionId) {
					return false;
				}
				return tx.get(SNAPSHOTS, key).then((stored) => {
					if(stored && compareVersions(record.version, stored.version) < 0) {
						return false;
					}
					return tx.put(SNAPSHOTS, key, record).then(() => true);
				});
			}));
		},

		/**
		 * The session for `userId`: the stored one when it is that user's, otherwise a new one, and then
		 * every record, op and result belonging to another user is deleted, since a different user has
		 * signed in on this browser.
		 */
		openSession(userId) {
			const stores = [SNAPSHOTS, META, 'ops', 'op_results'];
			return db.run(stores, 'readwrite', (tx) => tx.get(META, SESSION).then((current) => {
				if(current && sameUser(current.userId, userId)) {
					return current;
				}
				const session = { userId, sessionId: newSessionId() };
				return Promise.all([
					tx.put(META, SESSION, session),
					tx.keys(SNAPSHOTS).then((keys) => Promise.all(keys
						.filter((key) => {
							const parsed = parseRecordKey(key);
							return parsed && !sameUser(parsed.userId, userId);
						})
						.map((key) => tx.delete(SNAPSHOTS, key)))),
					...['ops', 'op_results'].map((store) => tx.entries(store).then((entries) => Promise.all(entries
						.filter(({ value }) => value && value.user_id !== undefined && !sameUser(value.user_id, userId))
						.map(({ key }) => tx.delete(store, key))))),
				]).then(() => session);
			}));
		},

		/** Removes the session marker, so no tab writes until someone signs in again. */
		endSession() {
			return db.run([META], 'readwrite', (tx) => tx.delete(META, SESSION));
		},

		/** The stored session marker, or null. */
		session() {
			return db.run([META], 'readonly', (tx) => tx.get(META, SESSION)).then((session) => session || null);
		},

		/**
		 * Deletes `namespace`'s records with a schema below `schema`. Higher ones are kept: they belong
		 * to a newer bundle open in another tab.
		 */
		removeBelow(namespace, schema) {
			return db.run([SNAPSHOTS], 'readwrite', (tx) => tx.keys(SNAPSHOTS).then((keys) => Promise.all(keys
				.filter((key) => {
					const parsed = parseRecordKey(key);
					return parsed && parsed.namespace === namespace && parsed.schema < schema;
				})
				.map((key) => tx.delete(SNAPSHOTS, key)))));
		},

		/** Keeps the `count` most recently saved of the user's records in `namespace` and `schema`. */
		keepNewest(namespace, schema, userId, count) {
			return db.run([SNAPSHOTS], 'readwrite', (tx) => tx.entries(SNAPSHOTS).then((entries) => {
				const mine = entries.filter(({ key }) => {
					const parsed = parseRecordKey(key);
					return parsed && parsed.namespace === namespace && parsed.schema === schema && sameUser(parsed.userId, userId);
				});
				mine.sort((a, b) => (Number(b.value && b.value.savedAt) || 0) - (Number(a.value && a.value.savedAt) || 0));
				return Promise.all(mine.slice(count).map(({ key }) => tx.delete(SNAPSHOTS, key)));
			}));
		},

		/** Empties every store. */
		clearAll() {
			return clearDb(db);
		},
	};
}
